<template>
  <div class="flex flex-col max-h-[500px] 2xl:max-h-[600px] w-[650px] transition-all">
    <div class="p-6 flex-1 flex flex-col overflow-hidden">
      <div>
        <h1 class="text-lg font-medium text-[#3A4557]"
          >Setup Your Brand Profile
          <i
          v-tooltip="'You can enter a website, write a short intro, upload a file, or connect your social accounts. We’ll generate a custom brand voice used for creating personalized content.'"
          class="far fa-question-circle p-0 cursor-pointer text-primary-cs ml-2"
        ></i>
          </h1
        >
        <p class="text-sm text-gray-700 mt-2">
          Use one or more of the sources below to help AI learn your brand’s voice.
        </p>
        <!-- Tabs -->
        <div class="flex items-center gap-x-4 mt-6">
          <!-- Tab Headers -->
          <div
            v-for="tab in tabs"
            :key="tab.id"
            :class="[
              selectedTab === tab.id ? 'cstu-text-blue-500 cstu-bg-blue-50' : '',
              'select-none text-sm rounded-full py-2 hover:cstu-bg-blue-50 px-3 flex items-center hover:cstu-text-blue-500 cursor-pointer gap-x-2',
            ]"
            @click="selectedTab = tab.id"
          >
            <i :class="[tab.icon]" aria-hidden="true"></i>
            <span class="text-base">{{ tab.name }}</span>
          </div>
        </div>
      </div>

      <!-- Tab Content - Scrollable Area -->
      <div class="mt-4 flex-1 overflow-y-auto no-scrollbar ">
            <WebsiteTab v-if="selectedTab === 'website'" />
            <TextTab v-else-if="selectedTab === 'text'" />
            <UploadsTab v-else-if="selectedTab === 'uploads'" />
            <SocialTab v-else-if="selectedTab === 'social'" />
      </div>
    </div>

    <!-- Footer - Sticky -->
    <div
      v-if="!showFooter"
      class="flex items-center justify-between px-8 py-6 border-gray-200 bg-white rounded-b-xl"
    >
      <a
      href="#"
      class="text-sm text-blue-500 beacon"
      data-beacon-article-modal="680f6c1a04f4b009e468e638"
      >About AI Library</a>

      <Button size="sm" :loading="isLoading" @click="handleAnalyze">
        Build My Brand Profile
      </Button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Button } from '@contentstudio/ui'
import WebsiteTab from './tabs/WebsiteTab.vue'
import TextTab from './tabs/TextTab.vue'
import UploadsTab from './tabs/UploadsTab.vue'
import SocialTab from './tabs/SocialTab.vue'
import { useSetup } from '@/src/modules/publisher/ai-content-library/composables/useSetup'
import { useStore } from '@/src/store/base'

const { isLoading, setupInfo, saveSetupInfo, validateWebsiteUrl: validateWebsiteUrlFn } = useSetup()
const { dispatch, getters } = useStore()

const hasSocialSetup = computed(() => {
  // Check if any social platform has selected accounts
  return getters.getSocialAccountsList?.length > 0
})

const selectedTab = ref('website')
const tabs = [
  {
    id: 'website',
    name: 'Website',
    icon: 'far fa-link',
  },
  {
    id: 'text',
    name: 'Text Description',
    icon: 'far fa-pencil',
  },
  {
    id: 'uploads',
    name: 'Upload Files',
    icon: 'far fa-cloud-upload',
  },
  {
    id: 'social',
    name: 'Social Accounts',
    icon: 'icon-social-accounts-cs',
  },
]

const showFooter = computed(() => {
  return selectedTab.value === 'social' && !hasSocialSetup.value
})

const validateWebsiteUrl = () => {
  const { isValid, message, url } = validateWebsiteUrlFn(setupInfo.website.url)

  if (!isValid) {
    dispatch('toastNotification', {
      message,
      type: 'error',
    })
    return false
  }

  // Update the URL with the processed value
  setupInfo.website.url = url
  return true
}

const handleAnalyze = async () => {
  if (showFooter.value) {
    selectedTab.value = 'social'
    return
  }

  // Validate website URL
  if (setupInfo.website.url && !validateWebsiteUrl()) {
    return
  }

  const hasSocialAccountSelected = Object.values(setupInfo.social).some(accounts => accounts.length > 0)

  // Validate if user has added any information
  if (!setupInfo.website.url && !setupInfo.text.content && !setupInfo.uploads.files.length && !hasSocialAccountSelected) {
    dispatch('toastNotification', {
      message: 'Please add at least one source of information',
      type: 'error',
    })
    return
  }

  const success = await saveSetupInfo()
  if (success) {
    emit('setup-complete')
  }
}

const emit = defineEmits(['setup-complete'])
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Ensure the content takes up space during transition */
.fade-enter-active,
.fade-leave-active {
  position: absolute; /* Or 'fixed' if it suits the layout better */
  width: 100%; /* Ensure it takes full width */
}
</style>
